import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useOrg } from '../app/OrgProvider';
import { RecorderOverlay, RecorderOverlayProvider, useRecorderOverlay } from '../recorder/RecorderOverlay';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { atLeast } from '../queries/orgs';
import { Wordmark } from './Clapper';
import { InboxMenu } from './InboxMenu';
import { AppLink, usePathname } from './Nav';
import { DownloadIcon, GearIcon, LinkIcon, RecordIcon, UploadIcon } from './icons';
import metactoMark from '../assets/metacto-mark.svg';
import { Button, cx } from './ui';

/**
 * The signed-in chrome: identity, the team you are on, and the few places you can go.
 * Deliberately shallow: Slate has one job and the nav should not imply
 * otherwise.
 */

const MENU_ITEM =
  'flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-body text-chalk-dim ' +
  'transition-colors duration-120 hover:bg-slate-800 hover:text-chalk';

function useDismissable(open: boolean, close: () => void) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [close, open]);
  return containerRef;
}

function NavLink({
  to,
  params,
  children,
}: {
  to: string;
  params?: Record<string, string>;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const resolved = params
    ? Object.entries(params).reduce((path, [key, value]) => path.replace(`$${key}`, value), to)
    : to;
  const active = resolved === '/' ? pathname === '/' : pathname === resolved;

  return (
    <AppLink
      to={to}
      params={params}
      className={cx(
        'rounded px-2.5 py-1.5 text-body transition-colors duration-120',
        active ? 'bg-slate-800 text-chalk' : 'text-chalk-dim hover:bg-slate-850 hover:text-chalk',
      )}
    >
      {children}
    </AppLink>
  );
}

function AccountMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useDismissable(open, () => setOpen(false));
  const name = displayNameOf(user);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption font-medium uppercase text-chalk-dim transition-colors duration-120 hover:border-slate-600 hover:text-chalk"
      >
        {name.slice(0, 1)}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-56 animate-fade-up rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-body text-chalk">{name}</p>
            <p className="truncate text-caption text-chalk-faint">{user?.email}</p>
          </div>
          <div className="my-1 h-px bg-hairline" />
          <AppLink to="/settings" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <GearIcon size={14} />
            Settings
          </AppLink>
          {/* The apps: Mac, Windows, the Chrome extension, the phone. The page
              leads with whichever one fits the machine this is being read on. */}
          <AppLink to="/download" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <DownloadIcon size={14} />
            Get the apps
          </AppLink>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className={MENU_ITEM}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Opens the recorder over whatever page this is; there is no recording page. */
function RecordButton() {
  const { open } = useRecorderOverlay();
  return (
    <Button variant="primary" size="md" className="gap-2" onClick={() => open()}>
      <RecordIcon size={11} className="text-record" />
      Record
    </Button>
  );
}

/** A video that already exists, in. The panel itself explains the paid seat. */
function UploadButton() {
  const { openUpload } = useRecorderOverlay();
  return (
    <Button size="md" className="gap-2" onClick={() => openUpload()} title="Upload a video">
      <UploadIcon size={13} className="text-chalk-dim" />
      <span className="hidden sm:inline">Upload</span>
    </Button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <RecorderOverlayProvider>
      <Shell>{children}</Shell>
      <RecorderOverlay />
    </RecorderOverlayProvider>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const { org, role } = useOrg();

  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="sticky top-0 z-20 border-b border-hairline bg-slate-950/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
          <AppLink to="/" className="mr-1" aria-label="Slate home">
            <Wordmark />
          </AppLink>

          <nav className="hidden items-center gap-0.5 md:flex">
            <NavLink to="/">Library</NavLink>
            <NavLink to="/links">Requests</NavLink>
            <NavLink to="/download">Apps</NavLink>
            {/* The team's recordings are simply the Library; the people and
                the plan live on the Subscription tab. One door each. */}
            {org && atLeast(role ?? undefined, 'admin') ? (
              <NavLink to="/settings/subscription">Team</NavLink>
            ) : null}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {org ? (
              <span className="hidden max-w-[12rem] truncate text-caption text-chalk-dim sm:inline" title={org.name}>
                {org.name}
              </span>
            ) : (
              <AppLink
                to="/settings/subscription"
                className="hidden items-center gap-1.5 rounded border border-signal/40 px-2.5 py-1.5 text-caption text-signal transition-colors duration-120 hover:border-signal hover:bg-signal/10 sm:flex"
              >
                Upgrade
              </AppLink>
            )}
            <UploadButton />
            <RecordButton />
            <InboxMenu />
            <AccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">{children}</main>

      <footer className="border-t border-hairline">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-5 text-caption text-chalk-faint sm:px-6">
          <span>Slate</span>
          <AppLink to="/links" className="flex items-center gap-1.5 hover:text-chalk-dim">
            <LinkIcon size={13} />
            Ask someone for a video
          </AppLink>
          <a
            href="https://www.metacto.com"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1.5 hover:text-chalk"
          >
            Built by
            <img src={metactoMark} alt="" width={14} height={15} className="h-[15px] w-[14px]" />
            Metacto
          </a>
        </div>
      </footer>
    </div>
  );
}
